import React from 'react';

import { BRAILLE } from '../symbols';
import { convertToDots } from '../utilities';

const BrailleInputRow = ({ children, padding }: { children: React.ReactNode; padding?: boolean }) => (
  <div className={`${padding ? 'pb-1' : ''} flex items-center justify-between`}>{children}</div>
);

type DotHighlight = 'correct' | 'incorrect';

interface BrailleInputDotProps {
  enabled: boolean;
  padding?: boolean;
  onToggle: () => void;
  highlight?: DotHighlight;
  readOnly?: boolean;
}

const BrailleInputDot = ({ enabled, padding, onToggle, highlight, readOnly }: BrailleInputDotProps) => {
  let dotClass: string;

  if (highlight === 'correct' && enabled) {
    dotClass = 'w-4 h-4 rounded-full bg-green-600';
  } else if (highlight === 'incorrect' && enabled) {
    dotClass = 'w-4 h-4 rounded-full bg-red-500';
  } else if (highlight === 'incorrect' && !enabled) {
    dotClass = 'w-4 h-4 rounded-full border-2 border-red-500';
  } else {
    dotClass = `w-4 h-4 rounded-full ${enabled ? 'bg-black' : 'border-2 border-gray-400'}`;
  }

  return (
    <div
      className={`${padding ? 'ml-1' : ''} flex w-6 h-6 items-center justify-center ${
        readOnly ? 'cursor-default' : 'cursor-pointer'
      }`}
      onClick={onToggle}
    >
      <div className={dotClass} />
    </div>
  );
};

interface BrailleInputProps {
  value: BRAILLE;
  onChange: (value: BRAILLE) => void;
  dotHighlights?: DotHighlight[];
}

// Dot index i (0-based, dot1..dot6) maps to bit (1 << (5 - i))
const toggleDot = (value: BRAILLE, dotIndex: number): BRAILLE => value ^ (1 << (5 - dotIndex));

export const BrailleInput = ({ value, onChange, dotHighlights }: BrailleInputProps) => {
  const [dot1, dot2, dot3, dot4, dot5, dot6] = convertToDots(value);
  const readOnly = dotHighlights !== undefined;

  return (
    <div className="flex flex-col items-center justify-center mr-2 mb-4 rounded">
      <BrailleInputRow padding={true}>
        <BrailleInputDot
          enabled={dot1}
          onToggle={() => onChange(toggleDot(value, 0))}
          highlight={dotHighlights?.[0]}
          readOnly={readOnly}
        />
        <BrailleInputDot
          enabled={dot4}
          padding={true}
          onToggle={() => onChange(toggleDot(value, 3))}
          highlight={dotHighlights?.[3]}
          readOnly={readOnly}
        />
      </BrailleInputRow>
      <BrailleInputRow padding={true}>
        <BrailleInputDot
          enabled={dot2}
          onToggle={() => onChange(toggleDot(value, 1))}
          highlight={dotHighlights?.[1]}
          readOnly={readOnly}
        />
        <BrailleInputDot
          enabled={dot5}
          padding={true}
          onToggle={() => onChange(toggleDot(value, 4))}
          highlight={dotHighlights?.[4]}
          readOnly={readOnly}
        />
      </BrailleInputRow>
      <BrailleInputRow>
        <BrailleInputDot
          enabled={dot3}
          onToggle={() => onChange(toggleDot(value, 2))}
          highlight={dotHighlights?.[2]}
          readOnly={readOnly}
        />
        <BrailleInputDot
          enabled={dot6}
          padding={true}
          onToggle={() => onChange(toggleDot(value, 5))}
          highlight={dotHighlights?.[5]}
          readOnly={readOnly}
        />
      </BrailleInputRow>
    </div>
  );
};
